// La lógica pura de "Recrear video" — ADR-2026-10-09-recrear-video-adentro-de-recrear y
// ADR-2026-10-16-recrear-video-con-nuestra-receta.
//
// El modo es la mitad de video de Recrear: un video de referencia (propio o una plantilla),
// las imágenes de lo que se quiere poner en él (persona, producto, prenda) y unas
// indicaciones opcionales. Genera por su ruta propia del backend (`/v1/videos/recrear`):
// el backend mira el video y decide solo qué hacer (cambiar el producto, la ropa o la
// persona), borra las marcas de agua y lo entrega en 1080.
//
// Acá va todo lo que no es pantalla: los topes, cuánto cobra y cómo viaja cada imagen. Sin
// React, para que los tests lo cubran sin montar nada.

/** La ruta del modo (`modesCatalog`, path `recrear-video`). */
export const RUTA_RECREAR_VIDEO = '/recrear-video';

/** Lo que va en `modo_utilizado`: rotula la fila del historial y la pestaña Historial
 *  filtra por esto. El backend lo tiene en `MODOS_CON_FILA_EN_HISTORIAL`. */
export const MODO_UTILIZADO = 'recrear_video';

/** Lo más corto que acepta: con menos no hay movimiento que transferir. */
export const DURACION_MIN_S = 4;
/**
 * Hasta cuánto de video se usa. Es el tope de entrada de Seedance 2.5 (profesional) MEDIDO,
 * no los 30 de su salida: el proveedor rechaza una entrada que llegue al tope de salida
 * (espejo de `TOPE_VIDEO_REFERENCIA_PROFESIONAL_S` del backend). Un video más largo se usa
 * hasta acá: el backend recorta el tramo desde el principio.
 */
export const DURACION_MAX_S = 29;
/** Las imágenes que acepta el creador (`reference_images`, máx. 6 en el backend). */
export const MAX_IMAGENES = 6;

/** Peso máximo del video: lo que ya acepta Shorts Studio, y lo que el worker baja sin apuro. */
export const MAX_VIDEO_MB = 100;

/** Qué es cada imagen. Va en la etiqueta: el backend la usa en la leyenda `@ImageN`. */
export type RolDeImagen = 'persona' | 'producto' | 'prenda' | 'otro';

export const ROLES_DE_IMAGEN: { id: RolDeImagen; etiqueta: string }[] = [
  { id: 'persona', etiqueta: 'Persona' },
  { id: 'producto', etiqueta: 'Producto' },
  { id: 'prenda', etiqueta: 'Prenda' },
  { id: 'otro', etiqueta: 'Otro' },
];

export const etiquetaDeRol = (rol: RolDeImagen): string =>
  ROLES_DE_IMAGEN.find((r) => r.id === rol)?.etiqueta ?? 'Otro';

/** Una imagen puesta en el panel. */
export type ImagenDeRecrear =
  | {
      id: string;
      /** Una modelo guardada o una referencia de la biblioteca: ya tiene su URL de nuestro Storage. */
      origen: 'url';
      rol: RolDeImagen;
      url: string;
      previewUrl: string;
      /** Una modelo guardada: al generar se re-firma su foto (la URL de la lista vence a la hora). */
      modeloId?: number;
    }
  | {
      id: string;
      origen: 'subida';
      rol: RolDeImagen;
      /** Path en `video-project-assets` (`<empresa>/agent-refs/…`). */
      storagePath: string;
      previewUrl: string;
    }
  | {
      id: string;
      origen: 'catalogo';
      rol: RolDeImagen;
      /** Id de la variante: el backend baja sus fotos y las nombra como vistas. */
      productId: string;
      nombre: string;
      previewUrl: string;
    };

/** Un rol por defecto según de dónde viene la imagen: del catálogo es un producto. */
export const rolInicial = (origen: 'subida' | 'catalogo'): RolDeImagen =>
  origen === 'catalogo' ? 'producto' : 'persona';

/**
 * Cuántos segundos dura lo que sale: lo que dura el video, hasta el tope. Lo de abajo del
 * mínimo no llega acá (la pantalla lo rechaza), pero el piso se aplica igual.
 */
export function duracionDeSalida(duracionDelVideo: number): number {
  if (!(duracionDelVideo > 0)) return DURACION_MIN_S;
  return Math.max(DURACION_MIN_S, Math.min(DURACION_MAX_S, Math.round(duracionDelVideo)));
}

/**
 * Créditos por segundo de video: espejo de `RECREAR_VIDEO_CR_POR_SEGUNDO` del backend
 * (`billing/costs.ts`). 10 s = 30 créditos.
 */
export const CREDITOS_POR_SEGUNDO = 3;

/** Lo que cobra: los segundos de salida × 3. Siempre sale en 1080. */
export function creditosDeRecrearVideo(duracionDelVideo: number): number {
  return duracionDeSalida(duracionDelVideo) * CREDITOS_POR_SEGUNDO;
}

const enSegundos = (n: number) => `${Math.round(n)} s`;

/**
 * Qué decir del video recién elegido: `bloquear` si es demasiado corto, `recortar` si
 * pasa el tope (se usa el principio, con aviso), `ok` si entra. Sin duración conocida
 * (un formato que el navegador no lee) entra: el backend lo mide y recorta igual.
 */
export function revisarDuracion(
  segundos: number,
): { accion: 'ok' } | { accion: 'recortar'; aviso: string } | { accion: 'bloquear'; motivo: string } {
  if (!(segundos > 0)) return { accion: 'ok' };
  if (segundos < DURACION_MIN_S) {
    return {
      accion: 'bloquear',
      motivo: `Ese video dura ${enSegundos(segundos)}: necesitamos al menos ${DURACION_MIN_S} s para copiar el movimiento.`,
    };
  }
  if (segundos > DURACION_MAX_S) {
    return {
      accion: 'recortar',
      aviso: `El video dura ${enSegundos(segundos)}: vamos a usar los primeros ${DURACION_MAX_S} s.`,
    };
  }
  return { accion: 'ok' };
}

/** Una foto como la recibe `/v1/videos/recrear`: por su link o, si es del catálogo, por su id. */
export type ReferenciaDeRecrear = { url: string; rol: RolDeImagen } | { product_id: string; rol: RolDeImagen };

/**
 * Las imágenes como las recibe el backend, en el orden del panel. Un producto del catálogo
 * viaja por id —el backend baja sus fotos de nuestro Storage, y un producto recién traído de
 * Tiendanube todavía tiene el link de la tienda—; una subida, por su URL firmada; una modelo
 * guardada o una referencia, por su URL. Una subida sin firma no viaja.
 */
export function referenciasParaRecrear(
  imagenes: ImagenDeRecrear[],
  firmadas: Record<string, string>,
): ReferenciaDeRecrear[] {
  return imagenes.slice(0, MAX_IMAGENES).flatMap((img): ReferenciaDeRecrear[] => {
    if (img.origen === 'catalogo') return [{ product_id: img.productId, rol: img.rol }];
    // Una modelo guardada usa su foto re-firmada si la hay; si no, el link con el que entró.
    if (img.origen === 'url') {
      const url = firmadas[img.id] ?? img.url;
      return url ? [{ url, rol: img.rol }] : [];
    }
    const url = firmadas[img.id];
    return url ? [{ url, rol: img.rol }] : [];
  });
}

/** Lo que Recrear le pasa al modo por `location.state`: un video recién subido o una plantilla. */
export interface EstadoDeRecrearVideo {
  /** El video que se subió en la card "Recreá tu propio video". */
  videoSubido?: VideoDeReferencia;
  /** El id de una de las plantillas de `plantillas.ts`. */
  plantillaId?: string;
}

/** El video puesto en el panel. */
export interface VideoDeReferencia {
  /** `propio` = lo subió el usuario (ya está en su carpeta); `plantilla` = es de Atlas. */
  origen: 'propio' | 'plantilla';
  /** Para dibujarlo: la firmada del propio, o la pública de la plantilla. */
  url: string;
  /** Sólo `propio`: el path en `video-project-assets`, que es lo que se firma al generar. */
  storagePath?: string;
  duracionSeg: number;
  nombre: string;
  /** Proporción del original (ancho / alto). Sin dato, vertical. */
  proporcion?: number;
}

/**
 * El formato de salida es el del original: un video horizontal recreado en 9:16 sale
 * recortado o con otra composición. Se redondea a los tres que tienen sentido en video.
 */
export function formatoDelVideo(proporcion: number | undefined): '9:16' | '1:1' | '16:9' {
  if (!proporcion || !Number.isFinite(proporcion) || proporcion < 0.85) return '9:16';
  if (proporcion > 1.2) return '16:9';
  return '1:1';
}
